import { useCallback, useState, VoidFunctionComponent } from 'react';
import { Text } from '@react-three/drei';
import { Screen } from '@/components/Screen';
import { useResponsive } from '@/hooks/useResponsive';
import { PlaystationButton, Icon } from '@/components/PlaystationButton';
import { BotQuizAnswer, BotQuizQuestion } from '@/lib/types';
import { garamondNarrowLight } from '@/lib/fonts';
import { MOSAIC_TRANSITION_DURATION } from '@/lib/globals';
import { useEffectContext } from '@/hooks/useEffectContext';

const ICONS: Icon[] = ['square', 'triangle', 'circle', 'x'];

interface Props {
    questions: BotQuizQuestion[];
    submitAnswers: (answers: BotQuizAnswer[]) => void;
}
export const BotQuiz: VoidFunctionComponent<Props> = ({ questions, submitAnswers }) => {
    const { setPixelate } = useEffectContext();
    const { responsiveValue } = useResponsive();
    const [quizAnswers, setQuizAnswers] = useState<BotQuizAnswer[]>([]);
    const [questionIndex, setQuestionIndex] = useState<number>(0);

    const transitionToNextQuestion = useCallback(() => {
        setPixelate(true);
        setTimeout(() => {
            setQuestionIndex(questionIndex + 1);
            setPixelate(false);
        }, MOSAIC_TRANSITION_DURATION * 1000);
    }, [questionIndex, setPixelate]);

    const submitAnswer = useCallback(
        (id: string, answer: string) => {
            const answers = [
                ...quizAnswers,
                {
                    id,
                    answer,
                },
            ];
            if (questionIndex + 1 === questions.length) {
                submitAnswers(answers);
            } else {
                transitionToNextQuestion();
                setQuizAnswers(answers);
            }
        },
        [questionIndex, questions.length, quizAnswers, submitAnswers, transitionToNextQuestion]
    );

    const { id, prompt, options } = questions[questionIndex];

    return (
        <>
            <Screen>
                <Text
                    position={[0, 1.7, 0]}
                    textAlign="center"
                    font={garamondNarrowLight}
                    fontSize={responsiveValue(0.4, 0.31)}
                    color="white"
                    maxWidth={responsiveValue(4.9, 3.8)}
                    lineHeight={responsiveValue(1.2, 1.1)}
                >
                    {prompt}
                </Text>
                <group position={[responsiveValue(-1.5, -1.65), responsiveValue(0, -0.3), 0]}>
                    {options.map(([answer], i) => (
                        <PlaystationButton
                            clickableAreaDimensions={[4.5, 0.7]}
                            key={i}
                            position={[0, -i * responsiveValue(0.8, 0.9), 0]}
                            maxWidth={responsiveValue(3.5, 3.2)}
                            icon={ICONS[i]}
                            label={answer}
                            action={() => submitAnswer(id, answer)}
                        />
                    ))}
                </group>
            </Screen>
        </>
    );
};
